import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { FileClock, ArrowRight, AlertTriangle } from 'lucide-react';
import api from '../../lib/api';

const fmtCLP = (n) => {
  if (n === null || n === undefined) return '$0';
  const num = Number(n);
  if (Number.isNaN(num)) return '$0';
  return num.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
};

/**
 * Widget de "Cheques por Pagar esta semana" — Iter 286.d
 *
 * Muestra:
 *  - Cantidad de cheques que vencen en los próximos 7 días
 *  - Cantidad de vencidos accionables (últimos 60 días)
 *  - Monto total combinado
 *  - Link directo al listado
 *
 * Es tarjeta clickable. Solo se renderiza si hay algo que mostrar.
 */
export function ChequesEstaSemanaWidget({ compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await api.get('/flujo-caja/cxp/cheques-por-pagar');
        if (!alive) return;
        const items = res.data?.items || [];
        const hoy = new Date();
        hoy.setHours(0, 0, 0, 0);
        const finSemana = new Date(hoy);
        finSemana.setDate(finSemana.getDate() + 7);
        const inicio60d = new Date(hoy);
        inicio60d.setDate(inicio60d.getDate() - 60);

        let semana = 0;
        let venc = 0;
        let montoSemana = 0;
        let montoVenc = 0;

        for (const it of items) {
          if (!it.fecha_vencimiento) continue;
          const fv = new Date(String(it.fecha_vencimiento).slice(0, 10));
          if (Number.isNaN(fv.getTime())) continue;
          if (fv >= hoy && fv <= finSemana) {
            semana += 1;
            montoSemana += Number(it.monto || 0);
          } else if (fv < hoy && fv >= inicio60d) {
            venc += 1;
            montoVenc += Number(it.monto || 0);
          }
        }
        setData({ semana, venc, montoSemana, montoVenc });
      } catch {
        if (alive) setData({ semana: 0, venc: 0, montoSemana: 0, montoVenc: 0 });
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const i = setInterval(load, 5 * 60 * 1000); // refresh cada 5 min
    return () => { alive = false; clearInterval(i); };
  }, []);

  if (loading || !data) return null;
  // No mostrar el widget si no hay nada (comportamiento silencioso, no ruido)
  if (data.semana === 0 && data.venc === 0) return null;

  const totalCount = data.semana + data.venc;
  const totalMonto = data.montoSemana + data.montoVenc;
  const hasVencidos = data.venc > 0;

  const borderCls = hasVencidos ? 'border-l-red-500' : 'border-l-amber-500';
  const iconBg = hasVencidos ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600';

  if (compact) {
    // Versión compacta para grid de KPIs (Dashboard General)
    return (
      <Link to="/contabilidad/cheques-por-pagar" data-testid="widget-cheques-semana-compact">
        <Card className={`border-l-4 ${borderCls} hover:shadow-md transition-all cursor-pointer`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <FileClock className="w-4 h-4 text-blue-600" />
              <span className="text-[10px] font-medium uppercase text-muted-foreground">Cheques 7d</span>
            </div>
            <div className="text-2xl font-bold text-blue-700">{totalCount}</div>
            <div className="text-xs text-muted-foreground truncate">
              {fmtCLP(totalMonto)}
              {hasVencidos && (
                <span className="ml-1 text-red-600 font-semibold">· {data.venc} vencido{data.venc > 1 ? 's' : ''}</span>
              )}
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  }

  // Versión completa (Dashboard Contabilidad)
  return (
    <Link to="/contabilidad/cheques-por-pagar" data-testid="widget-cheques-semana">
      <Card className={`border-l-4 ${borderCls} hover:shadow-md transition-all cursor-pointer`}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${iconBg}`}>
                <FileClock className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
                  Cheques por Pagar esta semana
                  {hasVencidos && (
                    <Badge variant="destructive" className="text-[10px] py-0 h-4">
                      <AlertTriangle className="w-3 h-3 mr-0.5" />
                      {data.venc} vencido{data.venc > 1 ? 's' : ''}
                    </Badge>
                  )}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {data.semana > 0
                    ? `${data.semana} vence${data.semana > 1 ? 'n' : ''} en próx. 7 días`
                    : 'Sin próximos vencimientos'}
                </p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-gray-400" />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <div className="text-xs text-muted-foreground">Esta semana</div>
              <div className="text-lg font-bold text-amber-700" data-testid="stat-monto-semana">
                {fmtCLP(data.montoSemana)}
              </div>
              <div className="text-[11px] text-muted-foreground">{data.semana} cheque{data.semana !== 1 ? 's' : ''}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Vencidos (60d)</div>
              <div className={`text-lg font-bold ${hasVencidos ? 'text-red-700' : 'text-gray-400'}`} data-testid="stat-monto-venc">
                {fmtCLP(data.montoVenc)}
              </div>
              <div className="text-[11px] text-muted-foreground">{data.venc} cheque{data.venc !== 1 ? 's' : ''}</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
